import { cn } from '@/lib/cn'

/**
 * Przełącznik segmentowy z ekranu „Role i uprawnienia" (Tak / Warunkowo / Nie,
 * Wszystkie / Zespół / Własne). Aktywny segment jest granatowy.
 *
 * Komponent jest LOKALNY dla modułu admin — design system go nie ma i świadomie
 * nie dokładamy go do `src/design` (zgłoszone w raporcie).
 */

export interface SegmentedOption<T extends string> {
  value: T
  label: string
  disabled?: boolean
  title?: string
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[]
  value: T | null
  onChange: (value: T) => void
  disabled?: boolean
  'aria-label'?: string
  className?: string
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={rest['aria-label']}
      className={cn(
        'inline-flex shrink-0 overflow-hidden rounded border border-line bg-card',
        disabled && 'opacity-55',
        className,
      )}
    >
      {options.map((option, index) => {
        const active = value === option.value
        const isDisabled = disabled || option.disabled
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={isDisabled}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={cn(
              'h-[28px] min-w-[62px] px-3 text-micro font-medium transition-colors duration-fast',
              index > 0 && 'border-l border-line',
              active ? 'bg-navy-900 text-white' : 'bg-card text-muted hover:text-ink',
              !active && !isDisabled && 'hover:bg-[#f4f7f9]',
              isDisabled && 'cursor-not-allowed',
              !isDisabled && 'focus-visible:am-focus focus:outline-none',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
